import { createContext, useEffect, useState } from "react";
import { api } from "../services/api";

export const SiteContext = createContext();

function getClient() {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // 1. Ambiente Local ou Staging (pega do path: /slug/...)
  const isDev =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes("sistemapercept.com.br") ||
    process.env.NODE_ENV === "development";

  if (isDev) {
    const pathParts = pathname.split("/").filter(Boolean);
    return pathParts[0] || null;
  }

  // 2. Ambiente de Produção
  const cleanHostname = hostname.replace(/^www\./i, "");
  const parts = cleanHostname.split(".").filter(Boolean);

  // Se o domínio for "transparencia.cliente.gov.br", pega o nome do cliente
  if (parts[0] === "transparencia" && parts.length > 1) {
    return parts[1];
  }

  return parts[0] || null;
}

// Função auxiliar para atualizar ou criar meta tags no <head>
function updateMetaTag(attribute, key, content) {
  let element = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

export function SiteProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  // ♿ 1. Estado de Alto Contraste (Persistido no localStorage)
  const [isHighContrast, setIsHighContrast] = useState(() => {
    return localStorage.getItem("highContrast") === "true";
  });

  // ♿ 2. Aplica ou remove a classe 'high-contrast' no <body> automaticamente
  useEffect(() => {
    if (isHighContrast) {
      document.body.classList.add("high-contrast");
    } else {
      document.body.classList.remove("high-contrast");
    }
  }, [isHighContrast]);

  // ♿ 3. Função para alternar o alto contraste
  const toggleContrast = () => {
    setIsHighContrast((prev) => {
      const newValue = !prev;
      localStorage.setItem("highContrast", String(newValue));
      return newValue;
    });
  };

  useEffect(() => {
    async function loadConfig() {
      try {
        const clientSlug = getClient();

        if (!clientSlug) {
          setLoading(false);
          return;
        }

        const { data } = await api.get(`/site-config?client=${clientSlug}`);

        if (data) {
          setConfig(data);

          // 🎨 1. Cores dinâmicas
          if (data.cor_primaria) {
            document.documentElement.style.setProperty("--primary", data.cor_primaria);
            updateMetaTag("name", "theme-color", data.cor_primaria);
          }

          if (data.cor_secundaria) {
            document.documentElement.style.setProperty("--secondary", data.cor_secundaria);
          }

          // Nome genérico como fallback para não engessar nenhum cliente
          const nomeCliente = data.nome || data.titulo || "Portal Oficial";

          // 🏷️ 2. Título dinâmico da guia (SEO)
          document.title = `${nomeCliente} - Portal da Transparência`;

          // 🏢 3. Dados estruturados para o Google definir o Site Name
          let scriptSchema = document.getElementById("schema-site-name");
          if (!scriptSchema) {
            scriptSchema = document.createElement("script");
            scriptSchema.id = "schema-site-name";
            scriptSchema.type = "application/ld+json";
            document.head.appendChild(scriptSchema);
          }

          scriptSchema.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": nomeCliente,
            "alternateName": [
              `Portal da Transparência - ${nomeCliente}`
            ],
            "url": window.location.origin
          });

          // Meta tag que ajuda o Google a saber o nome do site acima da URL
          updateMetaTag("property", "og:site_name", nomeCliente);

          // 🔍 4. Meta Description dinâmica (Google)
          const descricao = `Portal da Transparência de ${nomeCliente}. Consulte contas públicas, receitas, despesas, relatórios e publicações oficiais.`;
          updateMetaTag("name", "description", descricao);

          // 📱 5. Tags Open Graph (WhatsApp, redes sociais)
          updateMetaTag("property", "og:title", `${nomeCliente} - Portal da Transparência`);
          updateMetaTag("property", "og:description", descricao);
          updateMetaTag("property", "og:type", "website");
          updateMetaTag("property", "og:url", window.location.href);

          if (data.logo || data.brasao) {
            updateMetaTag("property", "og:image", data.logo || data.brasao);
          }

          // 🖼️ 6. Favicon dinâmico da aba (S3 / Banco de Dados)
          const iconUrl = data.favicon || data.logo;
          if (iconUrl) {
            let favicon = document.getElementById("dynamic-favicon");

            if (!favicon) {
              favicon = document.querySelector("link[rel~='icon']");
            }

            if (favicon) {
              favicon.href = iconUrl;
            } else {
              const newFavicon = document.createElement("link");
              newFavicon.id = "dynamic-favicon";
              newFavicon.rel = "icon";
              newFavicon.href = iconUrl;
              document.head.appendChild(newFavicon);
            }

            // Atualiza também o ícone para dispositivos móveis e robô mobile do Google
            let appleFavicon = document.querySelector("link[rel='apple-touch-icon']");
            if (appleFavicon) {
              appleFavicon.href = iconUrl;
            } else {
              const newAppleIcon = document.createElement("link");
              newAppleIcon.rel = "apple-touch-icon";
              newAppleIcon.href = iconUrl;
              document.head.appendChild(newAppleIcon);
            }
          }
        }
      } catch (err) {
        console.error("Erro ao carregar config:", err);
      } finally {
        setLoading(false);
      }
    }

    loadConfig();
  }, []);

  return (
    <SiteContext.Provider value={{ config, loading, isHighContrast, toggleContrast }}>
      {children}
    </SiteContext.Provider>
  );
}